import { lazy, Suspense, useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import Layout from "./components/Layout";
import { SUPPORTED_LANGUAGES, useLanguage } from "./context/LanguageContext";
import HomePage from "./pages/HomePage";
import NotFoundPage from "./pages/NotFoundPage";

const AboutPage = lazy(() => import("./pages/AboutPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
// TODO(enable-online-orders): uncomment to enable checkout
// const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const CmsCategoriesPage = lazy(() => import("./pages/cms/CmsCategoriesPage"));
const CmsColorsPage = lazy(() => import("./pages/cms/CmsColorsPage"));
const CmsCompositionsPage = lazy(() => import("./pages/cms/CmsCompositionsPage"));
const CmsLayout = lazy(() => import("./pages/cms/CmsLayout"));
const CmsOrdersPage = lazy(() => import("./pages/cms/CmsOrdersPage"));
const CmsProductFormPage = lazy(() => import("./pages/cms/CmsProductFormPage"));
const CmsProductListPage = lazy(() => import("./pages/cms/CmsProductListPage"));
const CmsSubcategoriesPage = lazy(() => import("./pages/cms/CmsSubcategoriesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const EmailConfirmPage = lazy(() => import("./pages/EmailConfirmPage"));
const HowToOrderPage = lazy(() => import("./pages/HowToOrderPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const PasswordChangePage = lazy(() => import("./pages/PasswordChangePage"));
const PasswordResetConfirmPage = lazy(() => import("./pages/PasswordResetConfirmPage"));
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));
const PersonalDataPage = lazy(() => import("./pages/PersonalDataPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ShippingAddressPage = lazy(() => import("./pages/ShippingAddressPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const CookiePolicyPage = lazy(() => import("./pages/LegalPage").then(m => ({ default: m.CookiePolicyPage })));
const PrivacyPolicyPage = lazy(() => import("./pages/LegalPage").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import("./pages/LegalPage").then(m => ({ default: m.TermsPage })));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function LanguageRoute() {
  const { lang } = useParams();
  const { setLanguage } = useLanguage();
  const supported = SUPPORTED_LANGUAGES.some(({ code }) => code === lang);

  useEffect(() => {
    if (supported) setLanguage(lang);
  }, [lang, setLanguage, supported]);

  return supported ? <Outlet /> : <NotFoundPage />;
}

function RootRedirect() {
  const { language } = useLanguage();
  return <Navigate to={`/${language}/`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/:lang" element={<LanguageRoute />}>
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="category" element={<CatalogPage />} />
            <Route path="products/:spu" element={<ProductPage />} />
            <Route path="cart" element={<CartPage />} />
            {/* TODO(enable-online-orders): uncomment to enable checkout route */}
            {/* <Route path="checkout" element={<CheckoutPage />} /> */}
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="howtoorder" element={<HowToOrderPage />} />
            <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="cookie-policy" element={<CookiePolicyPage />} />
            <Route path="terms-and-conditions" element={<TermsPage />} />
            <Route path="account/login" element={<LoginPage />} />
            <Route path="account/signup" element={<SignupPage />} />
            <Route path="account/reset-password" element={<PasswordResetPage />} />
            <Route path="account/reset-password/:uid/:token" element={<PasswordResetConfirmPage />} />
            <Route path="account/confirm-email/:key" element={<EmailConfirmPage />} />
            <Route path="account" element={<ProfilePage />}>
              <Route path="personal-data" element={<PersonalDataPage />} />
              <Route path="wishlist" element={<WishlistPage />} />
              <Route path="shipping-addresses" element={<ShippingAddressPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:orderId" element={<OrderDetailPage />} />
              <Route path="change-password" element={<PasswordChangePage />} />
              <Route index element={<PersonalDataPage />} />
            </Route>
            <Route path="cms" element={<CmsLayout />}>
              <Route path="products" element={<CmsProductListPage />} />
              <Route path="products/create" element={<CmsProductFormPage />} />
              <Route path="products/:productId/edit" element={<CmsProductFormPage />} />
              <Route path="categories" element={<CmsCategoriesPage />} />
              <Route path="subcategories" element={<CmsSubcategoriesPage />} />
              <Route path="colors" element={<CmsColorsPage />} />
              <Route path="compositions" element={<CmsCompositionsPage />} />
              <Route path="orders" element={<CmsOrdersPage />} />
              <Route index element={<CmsProductListPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </Suspense>
  );
}
